import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../AnimatedArcs';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';

export interface ICTABarHubSpotFormLightboxProps extends IBaseElementProps {
	/**
	 * Theme - Default: Dark
	 */
	theme?: 'Light' | 'Dark';
	/**
	 * Layout - Default: Horizontal
	 */
	layout?: 'Horizontal' | 'Vertical';
	/**
	 * Logo - Recommended Size: 180px x 65px - only used in Horizontal layout
	 */
	logo?: IImage;
	hsFormLightboxButton: IFormLightboxButtonProps;
	/**
	 * Horizontal CTA Text - only used in Horizontal layout
	 */
	horizontalText?: string;
	/**
	 * Vertical CTA Heading - only used in Vertical layout
	 */
	verticalHeading?: string;
	/**
	 * Vertical CTA Description - only used in Vertical layout
	 *
	 * @control richtext
	 */
	verticalDescription?: string;
	/**
	 * Enable Line Animations - only used in Vertical layout
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds - only used in Vertical layout
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels - only used in Vertical layout
	 */
	lineAnimationOffset?: number;
}

/**
 * Calian CTA Bar with HubSpot Form Lightbox
 * @icon welcome-widgets-menus
 * @block
 */
const CTABarHubSpotFormLightbox: React.FC<ICTABarHubSpotFormLightboxProps> = ({
	id,
	className,
	classNames = [],
	theme = 'Dark',
	layout = 'Horizontal',
	logo,
	hsFormLightboxButton,
	horizontalText,
	verticalHeading,
	verticalDescription,
	enableLineAnimations = true,
	lineAnimationDelay = 250,
	lineAnimationOffset = 150,
}) => {
	let content = null;
	let arcOneColor = 'var(--bs-white)';
	let arcTwoColor = 'var(--bs-primary)';
	if (theme === 'Light') {
		arcTwoColor = 'var(--bs-light-border)';
	}

	// These const are to just help an author out when switching between vertical and horizontal layouts.
	// For the sake of an improved user experience.
	const _horizontalText = horizontalText || verticalHeading;
	const _verticalHeading = verticalHeading || horizontalText;

	switch (layout) {
		case 'Horizontal':
			content = (
				<div className="calian-cta-bar__horizontal container-fluid">
					<div className="row">
						<div className="calian-cta-bar__horizontal--left col-12 col-lg-3 col-xl-2">
							{logo && (
								<div className="calian-cta-bar__horizontal--logo">
									<Image {...logo} className="w-100 h-auto" />
								</div>
							)}
						</div>
						<div className="calian-cta-bar__horizontal--center col-12 col-lg-6 col-xl-7">
							{_horizontalText && (
								<div className="calian-cta-bar__horizontal--text">
									<p>{_horizontalText}</p>
								</div>
							)}
						</div>
						<div className="calian-cta-bar__horizontal--right col-12 col-lg-3 col-xl-3">
							{hsFormLightboxButton && (
								<div className="calian-cta-bar__horizontal--button">
									<FormLightboxButton
										{...hsFormLightboxButton}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			);
			break;
		case 'Vertical':
			content = (
				<div className="calian-cta-bar__vertical container-fluid position-relative">
					<div className="row">
						<div className="calian-cta-bar__vertical--content col-12">
							{_verticalHeading && (
								<h2 className="calian-cta-bar__vertical--heading">
									{_verticalHeading}
								</h2>
							)}
							{verticalDescription && (
								<div
									className="calian-cta-bar__vertical--description"
									dangerouslySetInnerHTML={{
										__html: verticalDescription,
									}}
								/>
							)}
							{hsFormLightboxButton && (
								<div className="calian-cta-bar__vertical--button">
									<FormLightboxButton
										{...hsFormLightboxButton}
									/>
								</div>
							)}
						</div>
					</div>
					{enableLineAnimations && (
						<AnimatedArcs
							arcsStyle={'Component - Two Lines'}
							arcsPosition="Left"
							arcOneColor={arcOneColor}
							arcOneStrokeWidth={4}
							arcTwoColor={arcTwoColor}
							arcTwoStrokeWidth={3}
							animationDelay={lineAnimationDelay}
							animationOffset={lineAnimationOffset}
						/>
					)}
				</div>
			);
			break;
	}
	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-cta-bar',
					{
						[`theme-${kebabCase(theme)}`]: theme,
					},
					...classNames,
				)
			}
		>
			<div className="calian-cta-bar__container container-fluid">
				{content}
			</div>
		</div>
	);
};

export default CTABarHubSpotFormLightbox;
